import React, { useState } from 'react';
import { Table, Badge, Button, Modal, Spinner } from 'react-bootstrap';
import ImageGallery from 'react-image-gallery';
import { Review, ReviewStatus, getStatusBadge, getRatingStars } from '../../Data/Reviews';
import { formatDate } from '../AdminDataTable/AdminDataTable';
import './AdminReviewTable.css';
import { showSuccess, showError, showConfirm } from "../../../Utilities/sweetAlert";

interface AdminReviewTableProps {
  reviews: Review[];
  loading?: boolean;
  onAcceptReview: (reviewId: number) => Promise<boolean>;
  onRejectReview: (reviewId: number) => Promise<boolean>;
  emptyMessage?: string;
  showTrailInfo?: boolean; // Whether to show trail column (for pending reviews)
}

const AdminReviewTable: React.FC<AdminReviewTableProps> = ({
  reviews,
  loading = false,
  onAcceptReview,
  onRejectReview,
  emptyMessage = "No reviews found for this trail.",
  showTrailInfo = false
}) => {
  const [showPhotos, setShowPhotos] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([]);
  const [showReviewText, setShowReviewText] = useState(false);
  const [selectedReviewText, setSelectedReviewText] = useState<{ text: string, reviewId: number, userName: string } | null>(null);

  const [actionLoading, setActionLoading] = useState<{ [key: number]: 'accepting' | 'rejecting' | null }>({});

  const handleViewPhotos = (photos: string[]) => {
    if (photos.length > 0) {
      setSelectedPhotos(photos);
      setShowPhotos(true);
    }
  };

  const handleViewReviewText = (text: string, reviewId: number, userName: string) => {
    setSelectedReviewText({ text, reviewId, userName });
    setShowReviewText(true);
  };

  const handleAccept = async (reviewId: number) => {
    setActionLoading(prev => ({ ...prev, [reviewId]: 'accepting' }));
    try {
      const success = await onAcceptReview(reviewId);
      if (!success) {
        showError('Accept Failed', 'Failed to accept review. Please try again.');
      } else {
        showSuccess('Review Accepted', 'Review has been accepted successfully');
      }
    } finally {
      setActionLoading(prev => ({ ...prev, [reviewId]: null }));
    }
  };

  const handleReject = async (reviewId: number) => {
    const result = await showConfirm(
      'Reject Review?',
      'Are you sure you want to reject this review?',
      'Yes, reject it',
      'Cancel'
    );

    if (result.isConfirmed) {
      setActionLoading(prev => ({ ...prev, [reviewId]: 'rejecting' }));
      try {
        const success = await onRejectReview(reviewId);
        if (!success) {
          showError('Reject Failed', 'Failed to reject review. Please try again.');
        } else {
          showSuccess('Review Rejected', 'Review has been rejected successfully');
        }
      } finally {
        setActionLoading(prev => ({ ...prev, [reviewId]: null }));
      }
    }
  };

  const imageGalleryItems = selectedPhotos.map((url) => ({
    original: url,
    thumbnail: url,
  }));

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading reviews...</span>
        </Spinner>
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="text-center p-4 text-muted">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="admin-review-table">
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th style={{ width: showTrailInfo ? '6%' : '8%' }}>ID</th>
            {showTrailInfo && <th style={{ width: '12%' }}>Trail</th>}
            <th style={{ width: showTrailInfo ? '8%' : '10%' }}>Status</th>
            <th style={{ width: showTrailInfo ? '8%' : '10%' }}>Rating</th>
            <th style={{ width: showTrailInfo ? '12%' : '15%' }}>User</th>
            <th style={{ width: showTrailInfo ? '20%' : '25%' }}>Review Text</th>
            <th style={{ width: showTrailInfo ? '8%' : '10%' }}>Difficulty</th>
            <th style={{ width: showTrailInfo ? '6%' : '8%' }}>Photos</th>
            <th style={{ width: showTrailInfo ? '10%' : '12%' }}>Date</th>
            <th style={{ width: showTrailInfo ? '10%' : '12%' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {reviews.map((review) => {
            const statusBadge = getStatusBadge(review.status);
            const isActionLoading = actionLoading[review.id];

            return (
              <tr key={review.id}>
                <td>{review.id}</td>
                {showTrailInfo && (
                  <td>
                    {review.trail ? (
                      <div>
                        <strong>#{review.trail.id}</strong>
                        <br />
                        <small className="text-muted" title={review.trail.name}>
                          {review.trail.name.length > 20 ?
                            `${review.trail.name.substring(0, 20)}...` :
                            review.trail.name
                          }
                        </small>
                      </div>
                    ) : (
                      <em className="text-muted">Unknown Trail</em>
                    )}
                  </td>
                )}
                <td>
                  <Badge bg={statusBadge.variant}>
                    {statusBadge.text}
                  </Badge>
                </td>
                <td>
                  <div className="d-flex align-items-center">
                    <span className="me-1">{getRatingStars(review.rating)}</span>
                    <span>({review.rating})</span>
                  </div>
                </td>
                <td>
                  <div>
                    <strong>{review.user?.name || 'Unknown User'}</strong>
                    <br />
                    <small className="text-muted">ID: {review.user?.id}</small>
                  </div>
                </td>
                <td>
                  <div className="review-text-cell">
                    {review.text ? (
                      review.text.length > 100 ? (
                        <div>
                          <div className="mb-2">
                            {review.text.substring(0, 100)}...
                            <small className="text-muted d-block mt-1">
                              ({review.text.length} characters)
                            </small>
                          </div>
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => handleViewReviewText(review.text || '', review.id, review.user?.name || 'Unknown User')}
                          >
                            View Full Text
                          </Button>
                        </div>
                      ) : (
                        <div>
                          {review.text}
                          <small className="text-muted d-block mt-1">
                            ({review.text.length} characters)
                          </small>
                        </div>
                      )
                    ) : (
                      <em className="text-muted">No text provided</em>
                    )}
                  </div>
                </td>
                <td>
                  {review.difficulty ? (
                    <Badge bg="info">{review.difficulty}</Badge>
                  ) : (
                    <span className="text-muted">N/A</span>
                  )}
                </td>
                <td>
                  {review.photos.length > 0 ? (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleViewPhotos(review.photos)}
                    >
                      View ({review.photos.length})
                    </Button>
                  ) : (
                    <span className="text-muted">No photos</span>
                  )}
                </td>
                <td>
                  <small>{formatDate(review.createdAt)}</small>
                </td>
                <td>
                  <div className="action-buttons">
                    {review.status === ReviewStatus.Pending && (
                      <>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleAccept(review.id)}
                          disabled={!!isActionLoading}
                          className="me-1 mb-1"
                        >
                          {isActionLoading === 'accepting' ? (
                            <Spinner size="sm" animation="border" />
                          ) : (
                            'Accept'
                          )}
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleReject(review.id)}
                          disabled={!!isActionLoading}
                          className="mb-1"
                        >
                          {isActionLoading === 'rejecting' ? (
                            <Spinner size="sm" animation="border" />
                          ) : (
                            'Reject'
                          )}
                        </Button>
                      </>
                    )}
                    {review.status === ReviewStatus.Active && (
                      <Button
                        variant="warning"
                        size="sm"
                        onClick={() => handleReject(review.id)}
                        disabled={!!isActionLoading}
                      >
                        {isActionLoading === 'rejecting' ? (
                          <Spinner size="sm" animation="border" />
                        ) : (
                          'Deactivate'
                        )}
                      </Button>
                    )}
                    {review.status === ReviewStatus.Deleted && (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => handleAccept(review.id)}
                        disabled={!!isActionLoading}
                      >
                        {isActionLoading === 'accepting' ? (
                          <Spinner size="sm" animation="border" />
                        ) : (
                          'Reactivate'
                        )}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {/* Photo Gallery Modal */}
      <Modal
        show={showPhotos}
        onHide={() => setShowPhotos(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Review Photos</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPhotos.length > 0 && (
            <ImageGallery
              items={imageGalleryItems}
              showPlayButton={false}
              showFullscreenButton={true}
              showThumbnails={selectedPhotos.length > 1}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Review Text Modal */}
      <Modal
        show={showReviewText}
        onHide={() => setShowReviewText(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Full Review Text
            {selectedReviewText && (
              <div className="text-muted fs-6">
                Review #{selectedReviewText.reviewId} by {selectedReviewText.userName}
                <br />
                <small>{selectedReviewText.text.length} characters</small>
              </div>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedReviewText && (
            <div className="review-text-modal">
              <div className="review-text-content p-3 bg-light rounded">
                <p className="mb-0" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                  {selectedReviewText.text}
                </p>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AdminReviewTable;